import '../../../common/components/filter-slider/FilterSlider.less';

import React, { FC, memo, useContext } from 'react';
import { useReframe } from 'nike-re-framejs';

import NikeOneContext from '@/nikeone/context/NikeOneContext';
import i18n from '@i18n';
import CalendarIcon from '@/nikeone/components/sport-slider/components/calendar-icon/CalendarIcon';
import { FilterSwiper, NavigationFilterButton } from 'nike-ui/src/components/filterSwiper/FilterSwiper';
import { MatchMediaProvider } from 'nike-ui/src/components/turboLive/hooks/useMatchMedia';
import { generatePath } from 'react-router';
import { CURRENT_PAGE, NIKE_ONE_OVERVIEW_PATH, NIKE_ONE_RESULTS_PATH } from 'nike-ui/src/common/routing';
import SportSliderItem from '@/nikeone/components/sport-slider/SportSliderItem';
import usePathMatch from 'nike-ui/src/common/routing/usePathMatch';
import { SportIconNui } from 'nike-ui/src/icons/SportIconNui';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

interface SportSliderProps {
    slug: string;
    results: boolean;
}

const SportSlider: FC<SportSliderProps> = ({ slug, results }: { slug: string; results: boolean }) => {
    const derefSub = useReframe();
    const { favoritesOnly } = useContext(NikeOneContext);
    const sportItems = derefSub(['nikeone.sportList.filtered', favoritesOnly]);
    const defaultSportName = sportItems.size ? sportItems.get(0).get('icon') : null;
    const isTodayActive = slug => slug && slug.startsWith('/dnes');
    const calendarActive = isTodayActive(slug);
    const isSuperBetsActive = slug => slug === '/superkurzy';
    const superBetsActive = isSuperBetsActive(slug);

    if (!sportItems || !sportItems.size) {
        return null;
    }
    return (
        <MatchMediaProvider>
            <FilterSwiper
                staticButton={
                    <>
                        <SportSliderItem
                            label={i18n('jsp.homepage.datum.dnes')}
                            icon={CalendarIcon}
                            active={calendarActive}
                            key="today"
                            params={{ sportSlugPart: 'dnes' }}
                            trackData={{ label: 'Sport' }}
                            route={CURRENT_PAGE}
                        />
                        <SportSliderItem
                            label={i18n('prematchGame.superkurzy')}
                            icon="superkurzy"
                            active={superBetsActive}
                            key="superkurzy"
                            params={{ sportSlugPart: 'superkurzy' }}
                            trackData={{ label: 'Sport' }}
                            route={CURRENT_PAGE}
                        />
                    </>
                }
                refresh={sportItems}
                buttons={sportItems.map(sport => (
                    <SliderItem
                        key={sport.get('label')}
                        sportId={sport.get('sportId')}
                        sportName={sport.get('icon') || getSportNameById(sport.get('sportId'), false)}
                        label={sport.get('label')}
                        slug={sport.get('slug')}
                        results={results}
                        defaultSportName={defaultSportName}
                        calendarActive={calendarActive}
                        superBetsActive={superBetsActive}
                    />
                ))}
            />
        </MatchMediaProvider>
    );
};

const SliderItem = ({
    label,
    sportId,
    sportName,
    slug,
    activationChange,
    results,
    defaultSportName,
    calendarActive,
    superBetsActive
}: {
    label: string;
    sportId: string;
    sportName: string;
    slug: string;
    activationChange?: (HTMLElement) => void;
    results: boolean;
    defaultSportName: string;
    calendarActive: boolean;
    superBetsActive: boolean;
}) => {
    const pathSportName = slug?.replace('/', '');
    const nikeOneOverviewPath = generatePath(NIKE_ONE_OVERVIEW_PATH, { sportName: pathSportName });
    const nikeOneResultsPath = generatePath(NIKE_ONE_RESULTS_PATH, { sportName: pathSportName });
    const { isExact, isParent, isChild } = usePathMatch(results ? nikeOneResultsPath : nikeOneOverviewPath);
    const active =
        isExact || isParent || (isChild && sportName === defaultSportName && !calendarActive && !superBetsActive);

    return (
        <NavigationFilterButton
            activationChange={activationChange}
            title={label}
            path={results ? nikeOneResultsPath : nikeOneOverviewPath}
            icon={<SportIconNui sportId={sportId} sizeX="20" sizeY="20" className={`flex-shrink-0`} />}
            active={active}
        />
    );
};

export default memo(SportSlider);
